/**
 * SerialLookupReport (REPORT-SERIAL) — type a serial number (or model), find the
 * unit: which customer/property it's on, its model, install date and warranty.
 * The "a part came in under serial X, whose is it / is it under warranty"
 * lookup ESC makes painful. Reads the ESC equipment mirror (read-only).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, Download, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { classifyEquipment, CATEGORY_LABEL, type EquipmentRow } from "@/lib/equipment";
import { downloadCsv } from "./csv";

interface Hit extends EquipmentRow {
  custno: string; locno: string | null;
  customer?: string | null; address?: string | null;
}

export function SerialLookupReport() {
  const [term, setTerm] = useState("");
  const q = term.trim();

  const { data, isFetching } = useQuery({
    queryKey: ["serial_lookup", q.toLowerCase()],
    enabled: q.length >= 2,
    queryFn: async (): Promise<Hit[]> => {
      const { data: eq } = await supabase
        .from("mirror_equipment")
        .select("custno, locno, counter, mfg, model, serial, eq_type, equip_loc, install_date, warranty_date, ser_agr_no, notes, custom_1, custom_2, invoice")
        .or(`serial.ilike.%${q}%,model.ilike.%${q}%`)
        .limit(100);
      const rows = (eq ?? []) as Hit[];
      if (rows.length === 0) return [];
      const custnos = [...new Set(rows.map((r) => r.custno))];
      const [{ data: custs }, { data: locs }] = await Promise.all([
        supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
        supabase.from("mirror_location").select("custno, locno, add1, city").in("custno", custnos),
      ]);
      const cMap = new Map((custs ?? []).map((c: { custno: string; fullname: string }) => [c.custno, c.fullname]));
      const lMap = new Map((locs ?? []).map((l: { custno: string; locno: string; add1: string; city: string }) => [`${l.custno}|${l.locno}`, `${l.add1 ?? ""}${l.city ? ", " + l.city : ""}`]));
      return rows.map((r) => ({
        ...r,
        customer: cMap.get(r.custno) ?? r.custno,
        address: lMap.get(`${r.custno}|${r.locno}`) ?? null,
      }));
    },
  });

  const hits = useMemo(() => (data ?? []).map((h) => ({
    ...h, category: classifyEquipment(h).category,
  })), [data]);

  const exportCsv = () => downloadCsv("serial-lookup", [
    ["Serial", "Model", "Mfg", "Category", "Customer", "Address", "Installed", "Warranty", "Customer #"],
    ...hits.map((h) => [
      h.serial ?? "", h.model ?? "", h.mfg ?? "", CATEGORY_LABEL[h.category], h.customer ?? "",
      h.address ?? "", h.install_date ?? "", h.warranty_date ?? "", h.custno,
    ]),
  ]);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative min-w-[280px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
            placeholder="Serial number or model…"
            className="w-full rounded-md border border-hairline py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </div>
        {hits.length > 0 && (
          <button type="button" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Download className="h-4 w-4" /> CSV
          </button>
        )}
      </div>

      {q.length < 2 ? (
        <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-8 text-center text-sm text-slate-400">
          Type at least 2 characters of a serial number or model.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2">Serial</th><th className="px-3 py-2">Model</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Customer</th><th className="px-3 py-2">Installed</th><th className="px-3 py-2">Warranty</th><th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {isFetching && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">Searching…</td></tr>}
              {!isFetching && hits.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">No match.</td></tr>}
              {hits.map((h) => (
                <tr key={h.counter} className="border-b border-hairline last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs font-medium text-slate-800">{h.serial}</td>
                  <td className="px-3 py-2 text-slate-700">{h.model} <span className="text-xs text-slate-400">{h.mfg}</span></td>
                  <td className="px-3 py-2 text-slate-500">{CATEGORY_LABEL[h.category]}</td>
                  <td className="px-3 py-2 text-slate-700">{h.customer}<div className="text-xs text-slate-400">{h.address}</div></td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">{h.install_date}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">{h.warranty_date}</td>
                  <td className="px-3 py-2">
                    <Link to={`/customers/${h.custno}?loc=${h.locno ?? ""}&tab=equipment`} className="text-slate-300 hover:text-brand-600"><ExternalLink className="h-4 w-4" /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
